import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  contrastTextColorHex,
  getContrastTextColor,
  mixHexColors,
} from "@/utils/colorUtils";
import { CheckoutMarkdown } from "./CheckoutMarkdown";
import type { CheckoutAppearanceColors } from "./OrderQuantitySelector";

function formatTipAmountLabel({ amount }: { amount: number }): string {
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

export function CheckoutTipSelector({
  tipping,
  title = "Add tip",
  description,
  colors,
  onTipCentsChange,
  onTipSelectionChange,
  idPrefix = "checkout",
  highlightIncomplete = false,
}: {
  tipping?: {
    enabled: boolean;
    defaultSelected?: boolean;
    amount1?: number;
    amount2?: number;
    amount3?: number;
  };
  title?: string;
  description?: string;
  colors?: CheckoutAppearanceColors;
  onTipCentsChange?: ({ tipAmountCents }: { tipAmountCents: number }) => void;
  onTipSelectionChange?: ({ optedIn, hasSelected }: { optedIn: boolean; hasSelected: boolean }) => void;
  idPrefix?: string;
  highlightIncomplete?: boolean;
}) {
  const [tipOptIn, setTipOptIn] = useState(() => tipping?.defaultSelected === true);
  const [selectedTipAmount, setSelectedTipAmount] = useState<number | null>(null);
  const [customTipSelected, setCustomTipSelected] = useState(false);
  const [customTipAmount, setCustomTipAmount] = useState("");

  // Sync with defaultSelected if it changes (e.g. after loading from API)
  const lastDefaultSelected = useRef(tipping?.defaultSelected);
  useEffect(() => {
    if (tipping?.defaultSelected !== lastDefaultSelected.current) {
      lastDefaultSelected.current = tipping?.defaultSelected;
      setTipOptIn(tipping?.defaultSelected === true);
    }
  }, [tipping?.defaultSelected]);

  const hasSelectedTip = useMemo(() => {
    if (selectedTipAmount !== null) return true;
    if (customTipSelected) {
      const parsed = parseFloat(customTipAmount);
      return !Number.isNaN(parsed) && parsed >= 0;
    }
    return false;
  }, [selectedTipAmount, customTipSelected, customTipAmount]);

  const getTipAmount = useCallback(() => {
    if (!tipOptIn) return 0;
    if (customTipSelected) {
      const customAmount = parseFloat(customTipAmount);
      return Number.isNaN(customAmount) || customAmount < 0 ? 0 : customAmount;
    }
    if (selectedTipAmount !== null) return selectedTipAmount;
    return 0;
  }, [tipOptIn, customTipSelected, selectedTipAmount, customTipAmount]);

  const onTipChangeRef = useRef(onTipCentsChange);
  onTipChangeRef.current = onTipCentsChange;

  const onTipSelectionChangeRef = useRef(onTipSelectionChange);
  onTipSelectionChangeRef.current = onTipSelectionChange;

  useEffect(() => {
    const tipCents = Math.round(getTipAmount() * 100);
    onTipChangeRef.current?.({ tipAmountCents: tipCents });
  }, [tipOptIn, customTipSelected, selectedTipAmount, customTipAmount, getTipAmount]);

  useEffect(() => {
    onTipSelectionChangeRef.current?.({
      optedIn: tipOptIn,
      hasSelected: hasSelectedTip,
    });
  }, [tipOptIn, hasSelectedTip]);

  const checkoutTheme = useMemo(() => {
    if (!colors?.backgroundColor?.trim() && !colors?.accentColor?.trim()) {
      return null;
    }
    const surface = colors?.backgroundColor?.trim() || "#ffffff";
    const accent = colors?.accentColor?.trim() || "#6b7280";
    const primaryText = contrastTextColorHex(surface);
    const accentContrast = contrastTextColorHex(accent);
    const checkboxBorder = mixHexColors(accent, surface, 0.55);
    const focusRingClass =
      getContrastTextColor(surface) === "white"
        ? "focus-within:ring-white/45"
        : "focus-within:ring-black/30";
    return {
      surface,
      primaryText,
      focusRingClass,
      tipBg: surface,
      tipBorder: accent,
      tipTitleColor: primaryText,
      tipCheckboxBorder: checkboxBorder,
      tipCheckboxCheckedBg: accent,
      tipCheckboxCheckedFg: accentContrast,
    };
  }, [colors]);

  if (!tipping?.enabled) return null;

  const surface = colors?.backgroundColor?.trim() || "#ffffff";
  const accentColor = colors?.accentColor?.trim() || "#4b5563";
  const primaryText = checkoutTheme?.primaryText ?? contrastTextColorHex(surface);
  const accentText = contrastTextColorHex(accentColor);
  const checkboxId = `${idPrefix}-tip-opt-in`;

  const tipButtonStyle = (selected: boolean) =>
    selected
      ? {
        backgroundColor: accentColor,
        color: accentText,
        borderColor: accentColor,
      }
      : {
        backgroundColor: "transparent",
        color: primaryText,
        borderColor: accentColor,
      };

  const handleTipOptInChange = (checked: boolean) => {
    setTipOptIn(checked);
    if (!checked) {
      setSelectedTipAmount(null);
      setCustomTipSelected(false);
      setCustomTipAmount("");
    }
  };

  const isTipSelectionPending = tipOptIn && !hasSelectedTip;
  const needsAttention = highlightIncomplete && isTipSelectionPending;

  return (
    <label
      htmlFor={checkboxId}
      data-invalid={isTipSelectionPending ? "true" : undefined}
      className={cn(
        "flex w-full min-w-0 cursor-pointer flex-col gap-2 rounded-lg border-2 px-4 py-3.5 shadow-sm transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2",
        needsAttention && "ring-2 ring-red-500/25 border-red-500",
        !needsAttention && (checkoutTheme
          ? cn(
            "focus-within:ring-2 focus-within:ring-offset-2",
            checkoutTheme.focusRingClass
          )
          : "border-primary bg-transparent ring-offset-background hover:border-primary/80 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2")
      )}
      style={
        checkoutTheme
          ? {
            backgroundColor: checkoutTheme.tipBg,
            borderColor: needsAttention ? "#ef4444" : checkoutTheme.tipBorder,
            color: checkoutTheme.tipTitleColor,
            ["--checkout-accent" as string]: checkoutTheme.tipCheckboxCheckedBg,
            ["--checkout-accent-contrast" as string]:
              checkoutTheme.tipCheckboxCheckedFg,
            ["--checkout-checkbox-border" as string]:
              checkoutTheme.tipCheckboxBorder,
            ["--tw-ring-offset-color" as string]: checkoutTheme.surface,
          }
          : needsAttention
            ? { borderColor: "#ef4444" }
            : undefined
      }
    >
      <div className="flex w-full min-w-0 items-center gap-3">
        <Checkbox
          id={checkboxId}
          checked={tipOptIn}
          onCheckedChange={(checked) => handleTipOptInChange(checked === true)}
          className={cn(
            "size-5 shrink-0 rounded-[5px]",
            checkoutTheme &&
            "border-[color:var(--checkout-checkbox-border)] data-[state=checked]:border-[var(--checkout-accent)] data-[state=checked]:bg-[var(--checkout-accent)] data-[state=checked]:text-[var(--checkout-accent-contrast)]"
          )}
        />
        <span className="text-base font-semibold leading-snug">{title}</span>
      </div>
      {tipOptIn && description?.trim() ? (
        <div className="pl-8">
          <CheckoutMarkdown markdown={description} />
        </div>
      ) : null}
      {tipOptIn ? (
        <div className="space-y-3 pl-8">
          <div className="flex flex-wrap gap-2">
            {tipping.amount1 !== undefined ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedTipAmount(tipping.amount1!);
                  setCustomTipSelected(false);
                  setCustomTipAmount("");
                }}
                style={tipButtonStyle(selectedTipAmount === tipping.amount1)}
              >
                ${formatTipAmountLabel({ amount: tipping.amount1 })}
              </Button>
            ) : null}
            {tipping.amount2 !== undefined ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedTipAmount(tipping.amount2!);
                  setCustomTipSelected(false);
                  setCustomTipAmount("");
                }}
                style={tipButtonStyle(selectedTipAmount === tipping.amount2)}
              >
                ${formatTipAmountLabel({ amount: tipping.amount2 })}
              </Button>
            ) : null}
            {tipping.amount3 !== undefined ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedTipAmount(tipping.amount3!);
                  setCustomTipSelected(false);
                  setCustomTipAmount("");
                }}
                style={tipButtonStyle(selectedTipAmount === tipping.amount3)}
              >
                ${formatTipAmountLabel({ amount: tipping.amount3 })}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSelectedTipAmount(null);
                setCustomTipSelected(true);
              }}
              style={tipButtonStyle(customTipSelected)}
            >
              Custom
            </Button>
          </div>
          {customTipSelected ? (
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-custom-tip`} style={{ color: primaryText }}>
                Custom tip
              </Label>
              <Input
                id={`${idPrefix}-custom-tip`}
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={customTipAmount}
                onChange={(event) => setCustomTipAmount(event.target.value)}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </label>
  );
}
